import { GaugeIcon } from "lucide-react";

import type { ComposerPlanUsage } from "../composerPlanUsage";
import type { ComposerBannerStackItem } from "./ComposerBannerStack";

export function buildComposerUsageNoticeItem(input: {
  readonly key: string;
  readonly planUsage: ComposerPlanUsage | null;
  readonly credits: number | null;
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly onDismiss: () => void;
}): ComposerBannerStackItem {
  const { planUsage } = input;
  return {
    id: `usage:${input.key}`,
    variant: "info",
    priority: "notice",
    icon: <GaugeIcon />,
    title: planUsage
      ? `${planUsage.providerLabel} usage · ${planUsage.accountLabel ?? "Connected account"}${planUsage.planType ? ` · ${planUsage.planType}` : ""}`
      : "Account usage",
    description: usageNoticeDescription(input),
    dismissLabel: "Dismiss usage limits",
    onDismiss: input.onDismiss,
    children: planUsage ? (
      <ComposerUsageNoticeDetails planUsage={planUsage} credits={input.credits} />
    ) : undefined,
  };
}

function usageNoticeDescription(input: {
  readonly planUsage: ComposerPlanUsage | null;
  readonly isPending: boolean;
  readonly isError: boolean;
}): string | undefined {
  if (input.isError) return "Forge could not load account usage.";
  if (input.isPending) return "Loading account usage.";
  if (input.planUsage?.stale) return "Last observed usage may be out of date.";
  return input.planUsage && input.planUsage.windows.length > 0
    ? undefined
    : "No account usage has been reported for this thread yet.";
}

function ComposerUsageNoticeDetails(props: {
  readonly planUsage: ComposerPlanUsage;
  readonly credits: number | null;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-2 pt-1 text-sm" data-composer-usage-details>
      {props.planUsage.windows.map((window) => (
        <div key={`${window.kind}:${window.label}`} className="flex min-w-0 flex-col gap-0.5">
          <div className="flex min-w-0 items-baseline justify-between gap-3">
            <span className="min-w-0 truncate">{window.label}</span>
            <span className="shrink-0 tabular-nums text-muted-foreground">
              {window.usedPercent}% used, {window.remainingPercent}% left
            </span>
          </div>
          <time className="text-xs text-muted-foreground" dateTime={window.resetsAt}>
            Resets {formatUsageReset(window.resetsAt)}
          </time>
        </div>
      ))}
      {props.credits != null ? (
        <span className="tabular-nums text-muted-foreground">
          Credits: {props.credits.toLocaleString("en-US")} left
        </span>
      ) : null}
    </div>
  );
}

function formatUsageReset(value: string): string {
  const timestamp = Date.parse(value);
  if (Number.isNaN(timestamp)) return "time unavailable";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(timestamp);
}
